/**
 * iOS "Add to Home Screen" install prompt.
 *
 * iOS Safari doesn't fire `beforeinstallprompt`; the only path is a manual
 * prompt instructing the user to use Share → 加入主畫面.
 *
 * Sharp edges:
 *   - Detect iOS Safari (UA-based — imperfect, but the alternative is no
 *     prompt). iPadOS reports as Mac Safari; we detect via `maxTouchPoints`.
 *   - Skip if already running standalone (`display-mode: standalone` or
 *     iOS-specific `navigator.standalone`).
 *   - Persist dismissal to `localStorage` so we don't re-prompt every visit.
 */
import { useEffect, useState } from 'react';

import Icon from './Icon';

const DISMISS_KEY = 'mochia.install_prompt_dismissed';

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPhone / iPod / iPad explicitly
  const iOSDevice = /iPhone|iPod|iPad/.test(ua);
  // iPadOS 13+ reports as Mac; detect via touch points.
  const iPadOS =
    /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  if (!iOSDevice && !iPadOS) return false;
  // Exclude embedded WebView / Chrome on iOS (they can't add to home).
  if (/CriOS|FxiOS|EdgiOS/.test(ua)) return false;
  return /Safari/.test(ua);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
  return Boolean((navigator as NavigatorWithStandalone).standalone);
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function persistDismiss(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* localStorage might be disabled in private mode — silently ignore. */
  }
}

export default function InstallPrompt(): JSX.Element | null {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isIosSafari()) return;
    if (isStandalone()) return;
    if (readDismissed()) return;
    setVisible(true);
  }, []);

  if (!visible) return null;

  const handleClose = (): void => {
    persistDismiss();
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-live="polite"
      aria-label="加到主畫面"
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 100px)',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-2)',
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        zIndex: 150,
        font: '400 13px/1.5 var(--font-cjk)',
        color: 'var(--fg-1)',
      }}
    >
      <div style={{ flex: 1 }}>
        在 iPhone 加到主畫面：點選 ↑ 分享 → 加入主畫面
      </div>
      <button
        type="button"
        onClick={handleClose}
        aria-label="關閉提示"
        style={{
          width: 28,
          height: 28,
          borderRadius: 14,
          background: 'var(--bg-raised)',
          border: 0,
          cursor: 'pointer',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Icon name="x" size={14} stroke="var(--fg-2)" />
      </button>
    </div>
  );
}
